/**
 * IndexedDB Cache Service for GenFlappy
 *
 * This module provides browser-based asset caching using IndexedDB
 * with LRU eviction and graceful degradation for unsupported browsers.
 *
 * @module services/cacheService
 */

import type { CachedAsset, CacheConfig, CacheStats, CropMetadata } from '../types/cache';
import { validateAsset } from './qualityValidation';
import { generateCacheKey } from '../utils/hash';
import { findBestFuzzyMatch } from '../utils/fuzzyMatch';
import { detectQuotaExceeded, logError } from './errorDetectionService';
import { isStorageAccessible } from '../utils/storageDetection';

/**
 * Cache configuration
 */
const CACHE_CONFIG: CacheConfig = {
  dbName: 'genflappy-cache',
  storeName: 'assets',
  version: 1,
  maxQuotaMB: 50,
};

/**
 * Global cache disabled flag (set when quota exceeded or unsupported)
 */
let cacheDisabled = false;

/**
 * In-memory cache size tracker (bytes)
 */
let currentCacheSize = 0;

/**
 * Checks if IndexedDB and Web Crypto API are supported
 *
 * @returns {boolean} True if cache is supported, false otherwise
 */
export function isCacheSupported(): boolean {
  const hasIndexedDB = typeof indexedDB !== 'undefined';
  const hasCrypto = typeof crypto !== 'undefined' && typeof crypto.subtle !== 'undefined';
  return hasIndexedDB && hasCrypto;
}

/**
 * Initializes the IndexedDB cache database
 *
 * Creates the database "genflappy-cache" with object store "assets"
 * and indexes for "prompt", "timestamp", and "size".
 *
 * Gracefully degrades to null if IndexedDB or Web Crypto API are unavailable.
 *
 * @returns {Promise<IDBDatabase | null>} Database instance or null if unsupported
 *
 * @example
 * ```typescript
 * const db = await initCache();
 * if (db) {
 *   // Cache is ready
 * } else {
 *   // Cache not available - API-only mode
 * }
 * ```
 */
export async function initCache(): Promise<IDBDatabase | null> {
  // Check browser support
  if (!isCacheSupported()) {
    console.warn(
      'IndexedDB not supported - caching disabled. Application will function in API-only mode.'
    );
    return null;
  }

  // Story 7.2 - Task 4: Check storage accessibility (Safari ITP, Firefox ETP)
  const storageAccessible = await isStorageAccessible();
  if (!storageAccessible) {
    console.warn(
      'Storage blocked (Safari ITP / Firefox ETP / third-party restriction) - caching disabled. Application will function in API-only mode.'
    );
    cacheDisabled = true;
    return null;
  }

  return new Promise((resolve) => {
    try {
      const request = indexedDB.open(CACHE_CONFIG.dbName, CACHE_CONFIG.version);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        resolve(null);
      };

      request.onsuccess = () => {
        const db = request.result;
        console.log(`IndexedDB initialized: ${CACHE_CONFIG.dbName} v${CACHE_CONFIG.version}`);
        resolve(db);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(CACHE_CONFIG.storeName)) {
          const objectStore = db.createObjectStore(CACHE_CONFIG.storeName, {
            keyPath: 'id',
          });

          // Create indexes for efficient querying
          objectStore.createIndex('prompt', 'prompt', { unique: false });
          objectStore.createIndex('timestamp', 'timestamp', { unique: false });
          objectStore.createIndex('size', 'size', { unique: false });

          console.log('Created object store "assets" with indexes');
        }
      };
    } catch (error) {
      console.error('Error initializing IndexedDB:', error);
      resolve(null);
    }
  });
}

/**
 * Saves an asset to the IndexedDB cache
 *
 * Generates a cache key from the prompt and asset type, then stores
 * the asset with metadata (timestamp, size) in IndexedDB.
 *
 * @param {IDBDatabase} db - IndexedDB database instance
 * @param {string} prompt - User input prompt
 * @param {('character' | 'obstacle' | 'background')} assetType - Type of asset
 * @param {string} imageData - Base64 data URL of the image
 * @returns {Promise<void>}
 * @throws {Error} If cache write fails or quota exceeded
 *
 * @example
 * ```typescript
 * await saveAsset(db, "dragon", "character", "data:image/png;base64,...");
 * ```
 */
export async function saveAsset(
  db: IDBDatabase,
  prompt: string,
  assetType: 'character' | 'obstacle' | 'background',
  imageData: string,
  cropMetadata?: CropMetadata
): Promise<void> {
  // Generate cache key
  const id = await generateCacheKey(prompt, assetType);

  // Create asset object
  const asset: CachedAsset = {
    id,
    prompt,
    assetType,
    imageData,
    timestamp: Date.now(),
    size: imageData.length,
    cropMetadata,
  };

  // Quality validation before caching (Story 3.3)
  const validationResult = await validateAsset(imageData, assetType);
  if (!validationResult.passed) {
    console.warn('Asset failed validation:', validationResult.failureReasons);
    return; // Don't cache failed assets
  }

  return new Promise((resolve, reject) => {
    try {
      const transaction = db.transaction([CACHE_CONFIG.storeName], 'readwrite');
      const objectStore = transaction.objectStore(CACHE_CONFIG.storeName);

      // Check if asset already exists (for size tracking)
      const getRequest = objectStore.get(id);

      getRequest.onsuccess = () => {
        const existingAsset = getRequest.result as CachedAsset | undefined;
        const oldSize = existingAsset?.size || 0;

        const putRequest = objectStore.put(asset);

        putRequest.onsuccess = () => {
          // Update in-memory cache size tracker
          // Subtract old size if overwriting, then add new size
          currentCacheSize = currentCacheSize - oldSize + asset.size;

          // Log successful write
          console.log(`Cached ${assetType}: ${id.substring(0, 8)}...`);

          // Trigger LRU eviction if cache size >50MB
          if (currentCacheSize > CACHE_CONFIG.maxQuotaMB * 1024 * 1024) {
            evictOldestAssets(db, 45).catch((err) => {
              console.error('Eviction failed:', err);
            });
          }

          resolve();
        };

        putRequest.onerror = () => {
          const error = putRequest.error;

          // Handle QuotaExceededError
          if (error?.name === 'QuotaExceededError') {
            // Story 6.1: Detect and log quota exceeded error
            const quotaError = new DOMException('QuotaExceededError', 'QuotaExceededError');
            const errorContext = detectQuotaExceeded(quotaError);
            if (errorContext) {
              logError({
                ...errorContext,
                demoModeActivated: 'none', // Quota errors don't trigger demo mode
              });
            }

            handleQuotaExceeded(db).catch(() => {
              // Silent fail - already logged
            });
            reject(new Error('Cache quota exceeded'));
          } else {
            console.error('Failed to save asset:', error);
            reject(error);
          }
        };
      };

      getRequest.onerror = () => {
        reject(getRequest.error);
      };

      transaction.onerror = () => {
        reject(transaction.error);
      };
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Retrieves an asset from the IndexedDB cache
 *
 * Looks up an asset by generating a cache key from the prompt and asset type.
 *
 * @param {IDBDatabase} db - IndexedDB database instance
 * @param {string} prompt - User input prompt
 * @param {('character' | 'obstacle' | 'background')} assetType - Type of asset
 * @returns {Promise<CachedAsset | null>} The cached asset or null if not found
 *
 * @example
 * ```typescript
 * const asset = await getAsset(db, "dragon", "character");
 * if (asset) {
 *   // Use cached asset
 * } else {
 *   // Cache miss - generate via API
 * }
 * ```
 */
export async function getAsset(
  db: IDBDatabase,
  prompt: string,
  assetType: 'character' | 'obstacle' | 'background'
): Promise<CachedAsset | null> {
  // Generate cache key
  const id = await generateCacheKey(prompt, assetType);

  return new Promise((resolve, reject) => {
    try {
      const transaction = db.transaction([CACHE_CONFIG.storeName], 'readonly');
      const objectStore = transaction.objectStore(CACHE_CONFIG.storeName);

      const request = objectStore.get(id);

      request.onsuccess = () => {
        const asset = request.result as CachedAsset | undefined;

        if (asset && asset.imageData) {
          // Exact cache hit
          console.log(`Exact cache hit: ${assetType}`);

          // Update timestamp for LRU tracking (non-blocking)
          updateAccessTimestamp(db, id).catch(() => {
            // Silently fail - not critical
          });

          resolve(asset);
        } else {
          // Exact miss - try fuzzy matching
          const fuzzyStartTime = performance.now();

          findBestFuzzyMatch(db, prompt, assetType, 70)
            .then((result) => {
              const fuzzyDuration = performance.now() - fuzzyStartTime;

              // Performance monitoring: Log slow lookups
              if (fuzzyDuration > 200) {
                console.warn(`Slow fuzzy lookup: ${fuzzyDuration.toFixed(1)}ms`);
                // TODO: Track performance metrics when Story 8.3 complete
              }

              if (result.asset) {
                console.log(`Fuzzy cache hit (${result.bestScore.toFixed(1)}% match): ${assetType}`);
                // TODO: Integrate analytics when Story 8.2 complete

                // Update timestamp for LRU tracking (non-blocking)
                updateAccessTimestamp(db, result.asset.id).catch(() => {
                  // Silently fail - not critical
                });

                resolve(result.asset);
              } else {
                console.log(`Cache miss (best match: ${result.bestScore.toFixed(1)}%): ${assetType}`);
                // TODO: Integrate analytics when Story 8.2 complete
                resolve(null);
              }
            })
            .catch((error) => {
              console.error('Fuzzy matching error:', error);
              resolve(null); // Graceful degradation
            });
        }
      };

      request.onerror = () => {
        console.error('Failed to retrieve asset:', request.error);
        reject(request.error);
      };

      transaction.onerror = () => {
        reject(transaction.error);
      };
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Deletes an asset from the IndexedDB cache
 *
 * Removes an asset by its ID and updates the cache size tracker.
 *
 * @param {IDBDatabase} db - IndexedDB database instance
 * @param {string} id - Cache key (SHA-256 hash) of the asset to delete
 * @returns {Promise<void>}
 *
 * @example
 * ```typescript
 * const id = await generateCacheKey("dragon", "character");
 * await deleteAsset(db, id);
 * ```
 */
export async function deleteAsset(
  db: IDBDatabase,
  id: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      const transaction = db.transaction([CACHE_CONFIG.storeName], 'readwrite');
      const objectStore = transaction.objectStore(CACHE_CONFIG.storeName);

      // Get asset first to update cache size tracking
      const getRequest = objectStore.get(id);

      getRequest.onsuccess = () => {
        const asset = getRequest.result as CachedAsset | undefined;

        // Delete the asset
        const deleteRequest = objectStore.delete(id);

        deleteRequest.onsuccess = () => {
          // Update cache size tracking if asset existed
          if (asset) {
            currentCacheSize -= asset.size;
          }

          // Log deletion
          console.log(`Evicted asset: ${id.substring(0, 8)}...`);
          resolve();
        };

        deleteRequest.onerror = () => {
          console.error('Failed to delete asset:', deleteRequest.error);
          reject(deleteRequest.error);
        };
      };

      getRequest.onerror = () => {
        reject(getRequest.error);
      };

      transaction.onerror = () => {
        reject(transaction.error);
      };
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Gets cache statistics
 *
 * Calculates total size, asset count, oldest timestamp, and quota usage.
 *
 * @param {IDBDatabase} db - IndexedDB database instance
 * @returns {Promise<CacheStats>} Statistics about current cache state
 *
 * @example
 * ```typescript
 * const stats = await getCacheStats(db);
 * console.log(`Cache: ${stats.assetCount} assets, ${stats.quotaUsedPercent}% full`);
 * ```
 */
export async function getCacheStats(
  db: IDBDatabase
): Promise<CacheStats> {
  return new Promise((resolve, reject) => {
    try {
      const transaction = db.transaction([CACHE_CONFIG.storeName], 'readonly');
      const objectStore = transaction.objectStore(CACHE_CONFIG.storeName);

      let totalSize = 0;
      let assetCount = 0;
      let oldestTimestamp = Date.now();

      const request = objectStore.openCursor();

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;

        if (cursor) {
          const asset = cursor.value as CachedAsset;
          totalSize += asset.size;
          assetCount++;
          if (asset.timestamp < oldestTimestamp) {
            oldestTimestamp = asset.timestamp;
          }
          cursor.continue();
        } else {
          // Cursor finished - calculate stats
          const maxQuotaBytes = CACHE_CONFIG.maxQuotaMB * 1024 * 1024;
          const quotaUsedPercent = maxQuotaBytes > 0
            ? (totalSize / maxQuotaBytes) * 100
            : 0;

          resolve({
            totalSize,
            assetCount,
            oldestTimestamp: assetCount > 0 ? oldestTimestamp : 0,
            quotaUsedPercent: Math.min(100, quotaUsedPercent),
          });
        }
      };

      request.onerror = () => {
        console.error('Failed to get cache stats:', request.error);
        reject(request.error);
      };

      transaction.onerror = () => {
        reject(transaction.error);
      };
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Evicts oldest assets via LRU algorithm
 *
 * Deletes assets starting with the oldest (by timestamp) until cache
 * size is reduced to target size (default: 45MB = 90% of 50MB quota).
 *
 * @param {IDBDatabase} db - IndexedDB database instance
 * @param {number} targetSizeMB - Target cache size in MB (default: 45MB)
 * @returns {Promise<number>} Number of assets evicted
 *
 * @example
 * ```typescript
 * const evicted = await evictOldestAssets(db, 45);
 * console.log(`Evicted ${evicted} assets`);
 * ```
 */
export async function evictOldestAssets(
  db: IDBDatabase,
  targetSizeMB: number = 45
): Promise<number> {
  return new Promise((resolve, reject) => {
    try {
      const transaction = db.transaction([CACHE_CONFIG.storeName], 'readwrite');
      const objectStore = transaction.objectStore(CACHE_CONFIG.storeName);
      const index = objectStore.index('timestamp');

      // Get all assets sorted by timestamp (oldest first)
      const assets: CachedAsset[] = [];
      const request = index.openCursor();

      request.onsuccess = async (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;

        if (cursor) {
          assets.push(cursor.value as CachedAsset);
          cursor.continue();
        } else {
          // Calculate current size and determine how many to delete
          const currentSize = assets.reduce((sum, a) => sum + a.size, 0);
          const targetSizeBytes = targetSizeMB * 1024 * 1024;

          if (currentSize <= targetSizeBytes) {
            // Already under target
            resolve(0);
            return;
          }

          // Delete oldest until under target
          let freedSpace = 0;
          let evictedCount = 0;
          const toDelete: string[] = [];

          for (const asset of assets) {
            if (currentSize - freedSpace <= targetSizeBytes) {
              break;
            }
            toDelete.push(asset.id);
            freedSpace += asset.size;
            evictedCount++;
          }

          // Perform deletions
          for (const id of toDelete) {
            await deleteAsset(db, id);
          }

          const freedMB = (freedSpace / (1024 * 1024)).toFixed(2);
          console.log(`Evicted ${evictedCount} assets, freed ${freedMB}MB`);

          resolve(evictedCount);
        }
      };

      request.onerror = () => {
        console.error('Failed to evict assets:', request.error);
        reject(request.error);
      };

      transaction.onerror = () => {
        reject(transaction.error);
      };
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Updates the access timestamp for an asset (LRU tracking)
 *
 * Updates the timestamp to mark when an asset was last accessed,
 * ensuring frequently-used assets aren't evicted.
 *
 * @param {IDBDatabase} db - IndexedDB database instance
 * @param {string} id - Cache key of the asset
 * @returns {Promise<void>}
 *
 * @example
 * ```typescript
 * await updateAccessTimestamp(db, assetId);
 * ```
 */
export async function updateAccessTimestamp(
  db: IDBDatabase,
  id: string
): Promise<void> {
  return new Promise((resolve) => {
    try {
      const transaction = db.transaction([CACHE_CONFIG.storeName], 'readwrite');
      const objectStore = transaction.objectStore(CACHE_CONFIG.storeName);

      const getRequest = objectStore.get(id);

      getRequest.onsuccess = () => {
        const asset = getRequest.result as CachedAsset | undefined;

        if (asset) {
          asset.timestamp = Date.now();
          objectStore.put(asset);
        }

        // Non-blocking - resolve immediately
        resolve();
      };

      getRequest.onerror = () => {
        // Non-blocking - log but don't reject
        console.warn('Failed to update timestamp:', getRequest.error);
        resolve();
      };
    } catch (error) {
      // Non-blocking
      console.warn('Error updating timestamp:', error);
      resolve();
    }
  });
}

/**
 * Handles quota exceeded error - graceful degradation
 *
 * Disables caching globally and clears all cached assets when browser
 * quota is exceeded. Application continues in API-only mode.
 *
 * @param {IDBDatabase} db - IndexedDB database instance
 * @returns {Promise<void>}
 *
 * @example
 * ```typescript
 * await handleQuotaExceeded(db);
 * ```
 */
export async function handleQuotaExceeded(db: IDBDatabase): Promise<void> {
  // Set global flag
  cacheDisabled = true;

  console.warn('Cache quota exceeded - caching disabled. Application will function in API-only mode.');

  // Story 8.1: Log quota exceeded to Sentry via errorDetectionService
  logError({
    errorType: 'quota',
    timestamp: Date.now(),
    message: `IndexedDB quota exceeded - cacheSize: ${currentCacheSize}, quotaLimit: ${CACHE_CONFIG.maxQuotaMB * 1024 * 1024}`,
    severity: 'critical',
    demoModeActivated: 'none',
  });

  // Emergency cleanup - clear all cached assets
  try {
    const transaction = db.transaction([CACHE_CONFIG.storeName], 'readwrite');
    const objectStore = transaction.objectStore(CACHE_CONFIG.storeName);
    
    await new Promise<void>((resolve, reject) => {
      const request = objectStore.clear();
      request.onsuccess = () => {
        currentCacheSize = 0;
        console.log('Emergency cache cleanup completed');
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Failed to clear cache during quota exceeded:', error);
  }
}

/**
 * Checks if caching is currently disabled
 *
 * @returns {boolean} True if cache is disabled
 */
export function isCacheDisabled(): boolean {
  return cacheDisabled;
}
